import * as THREE from "three";
import {
  FIXED_STEP_SECONDS,
  ENTER_RADIUS_UNITS,
  GOAL_OBJECT_NAME,
  GOAL_OBJECT_RADIUS_METERS,
  GOAL_REQUIRED_RADIUS_METERS,
  MAX_ATTACHED_INSTANCES,
  MAX_CATCH_UP_STEPS,
  MAX_WORLD_INSTANCES,
  PICKUP_RADIUS_RATIO,
  SKYTREE_X_METERS,
  SKYTREE_Z_METERS,
  TIERS,
  TRANSITION_DURATION_SECONDS,
  WORLD_SEED,
  getTier,
  tierProgress,
} from "../core/config";
import { GrowthModel, type AbsorbResult } from "../core/GrowthModel";
import { QualityController, type QualityLevel, type QualitySettings } from "../core/QualityController";
import {
  clamp,
  damp,
  formatPhysicalSize,
  logLerp,
  navigationMetrics,
  smoothstep,
  squaredDistance,
} from "../core/math";
import { hashNumbers } from "../core/random";
import {
  INITIAL_HOME_EXIT_METERS,
  constrainInitialHomeMovement,
  terrainHeightMeters,
} from "../core/TokyoTerrain";
import type { WorldItem } from "../core/WorldGenerator";
import { AttachmentCloud } from "../render/AttachmentCloud";
import { Environment } from "../render/Environment";
import { GoalLandmark } from "../render/GoalLandmark";
import { ParticleBurst } from "../render/ParticleBurst";
import { WorldManager } from "../render/WorldManager";
import { AudioEngine } from "./AudioEngine";
import { DashModel } from "./DashModel";
import { frameTiming } from "./FrameTiming";
import { InputController, type MovementInput } from "./InputController";
import { Metrics } from "./Metrics";
import { buildResultShareUrl, formatResultTime } from "./ResultShare";
import { RunStats, type RunResult, type ResultRank } from "./RunStats";

interface TransitionState {
  readonly fromTierIndex: number;
  readonly toTierIndex: number;
  readonly fromScale: number;
  elapsedSeconds: number;
  progress: number;
}

const HOME_TUTORIAL_PICKUPS = 6;

interface UiElements {
  readonly app: HTMLElement;
  readonly intro: HTMLElement;
  readonly startButton: HTMLButtonElement;
  readonly pauseButton: HTMLButtonElement;
  readonly pauseScreen: HTMLElement;
  readonly resumeButton: HTMLButtonElement;
  readonly soundButton: HTMLButtonElement;
  readonly sizeValue: HTMLElement;
  readonly sizeUnit: HTMLElement;
  readonly tierIndex: HTMLElement;
  readonly tierName: HTMLElement;
  readonly progressFill: HTMLElement;
  readonly progressTrack: HTMLElement;
  readonly progressLabel: HTMLElement;
  readonly progressValue: HTMLElement;
  readonly pickupCount: HTMLElement;
  readonly scoreValue: HTMLElement;
  readonly timerValue: HTMLElement;
  readonly rareValue: HTMLElement;
  readonly comboValue: HTMLElement;
  readonly dashPanel: HTMLElement;
  readonly dashFill: HTMLElement;
  readonly dashLabel: HTMLElement;
  readonly fpsValue: HTMLElement;
  readonly instanceValue: HTMLElement;
  readonly drawValue: HTMLElement;
  readonly qualityValue: HTMLElement;
  readonly telemetry: HTMLElement;
  readonly objectivePanel: HTMLElement;
  readonly objectiveStep: HTMLElement;
  readonly objectiveTitle: HTMLElement;
  readonly objectiveDetail: HTMLElement;
  readonly objectiveAnnouncer: HTMLElement;
  readonly navigationArrow: HTMLElement;
  readonly navigationTarget: HTMLElement;
  readonly navigationDistance: HTMLElement;
  readonly pickupToast: HTMLElement;
  readonly pickupIcon: HTMLElement;
  readonly pickupName: HTMLElement;
  readonly pickupPoints: HTMLElement;
  readonly shiftOverlay: HTMLElement;
  readonly shiftName: HTMLElement;
  readonly shiftScale: HTMLElement;
  readonly touchControls: HTMLElement;
  readonly joystick: HTMLElement;
  readonly joystickKnob: HTMLElement;
  readonly boostButton: HTMLButtonElement;
  readonly finaleScreen: HTMLElement;
  readonly finaleSize: HTMLElement;
  readonly finaleRank: HTMLElement;
  readonly finaleTime: HTMLElement;
  readonly finalePickups: HTMLElement;
  readonly finaleRares: HTMLElement;
  readonly finaleTimeBonus: HTMLElement;
  readonly finaleScore: HTMLElement;
  readonly shareButton: HTMLAnchorElement;
  readonly continueButton: HTMLButtonElement;
  readonly restartButton: HTMLButtonElement;
}

export interface GameSnapshot {
  readonly schemaVersion: "2.0.0";
  readonly ready: boolean;
  readonly running: boolean;
  readonly paused: boolean;
  readonly tierIndex: number;
  readonly tierName: string;
  readonly radiusMeters: number;
  readonly diameterMeters: number;
  readonly localRadius: number;
  readonly renderMetersPerUnit: number;
  readonly viewRadiusMeters: number;
  readonly positionMeters: Readonly<{ x: number; z: number }>;
  readonly cameraYawRadians: number;
  readonly velocityMetersPerSecond: number;
  readonly pickupCount: number;
  readonly score: number;
  readonly runTimeSeconds: number;
  readonly rank: ResultRank | null;
  readonly rareCount: number;
  readonly combo: number;
  readonly comboMultiplier: number;
  readonly dashActive: boolean;
  readonly dashReady: boolean;
  readonly dashCharge: number;
  readonly surfaceHeightMeters: number;
  readonly homeExited: boolean;
  readonly audioVoices: number;
  readonly audioMusicActive: boolean;
  readonly shareUrl: string | null;
  readonly completed: boolean;
  readonly objectivePhase: ObjectivePhase;
  readonly objectiveTitle: string;
  readonly navigationDistanceMeters: number | null;
  readonly navigationBearingRadians: number | null;
  readonly goalVisible: boolean;
  readonly goalReady: boolean;
  readonly goalAbsorbing: boolean;
  readonly goalAbsorbed: boolean;
  readonly goalRadiusMeters: number;
  readonly goalRequiredRadiusMeters: number;
  readonly goalPositionMeters: Readonly<{ x: number; z: number }> | null;
  readonly transitioning: boolean;
  readonly transitionProgress: number;
  readonly activeInstances: number;
  readonly allocatedSlots: number;
  readonly gpuInstanceCapacity: number;
  readonly maximumSteadyInstances: number;
  readonly attachedInstances: number;
  readonly maximumAttachedInstances: number;
  readonly collisionCandidates: number;
  readonly drawCalls: number;
  readonly triangles: number;
  readonly geometries: number;
  readonly textures: number;
  readonly fps: number;
  readonly frameAverageMs: number;
  readonly frameP95Ms: number;
  readonly frameP99Ms: number;
  readonly quality: QualityLevel;
  readonly autoplay: boolean;
  readonly stateHash: number;
  readonly seed: number;
}

export interface SolDebugApi {
  snapshot(): GameSnapshot;
  collectNearest(): GameSnapshot;
  collideOversized(): OversizedCollisionProbe;
  collideGoal(): GoalCollisionProbe;
  forcePickup(count?: number): GameSnapshot;
  forceRarePickup(): GameSnapshot;
  triggerDash(): GameSnapshot;
  forceGoalReady(): GameSnapshot;
  forceTier(tierIndex: number): GameSnapshot;
  step(frames?: number): GameSnapshot;
  setCameraYaw(yawRadians: number): GameSnapshot;
  setAutoplay(enabled: boolean): GameSnapshot;
  restart(): GameSnapshot;
  shareUrl(): string | null;
}

export type ObjectivePhase =
  | "GROW"
  | "HOME_EXIT"
  | "FINAL_GROW"
  | "FINAL_READY"
  | "ABSORBING"
  | "CLEAR";

export interface GoalCollisionProbe {
  readonly targetFound: boolean;
  readonly goalReadyBefore: boolean;
  readonly goalAbsorbingAfter: boolean;
  readonly goalAbsorbedAfter: boolean;
  readonly before: GameSnapshot;
  readonly after: GameSnapshot;
}

export interface OversizedCollisionProbe {
  readonly targetFound: boolean;
  readonly targetId: number | null;
  readonly targetRadiusMeters: number | null;
  readonly maximumPickupRadiusMeters: number;
  readonly targetWasOversized: boolean;
  readonly targetActiveAfter: boolean;
  readonly pickupDelta: number;
  readonly before: GameSnapshot;
  readonly after: GameSnapshot;
}

const mustElement = <T extends HTMLElement>(id: string): T => {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Required element #${id} was not found`);
  }
  return element as T;
};

const colorCss = (hex: number): string => `#${hex.toString(16).padStart(6, "0")}`;

export class Game {
  private readonly canvas: HTMLCanvasElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(51, 1, 0.02, 220);
  private readonly growth = new GrowthModel();
  private readonly quality: QualityController;
  private readonly metrics = new Metrics();
  private readonly audio = new AudioEngine();
  private readonly dash = new DashModel();
  private readonly ui: UiElements;
  private readonly input: InputController;
  private readonly environment: Environment;
  private readonly world: WorldManager;
  private readonly attachmentCloud = new AttachmentCloud();
  private readonly particles = new ParticleBurst();
  private readonly goalLandmark = new GoalLandmark(GOAL_OBJECT_RADIUS_METERS);
  private readonly ballRoot = new THREE.Group();
  private readonly ballCore: THREE.Mesh;
  private readonly ballWire: THREE.Mesh;
  private readonly ballInner: THREE.Mesh;
  private readonly shadow: THREE.Mesh;
  private readonly rollAxis = new THREE.Vector3();
  private readonly desiredCamera = new THREE.Vector3();
  private readonly cameraTarget = new THREE.Vector3();
  private readonly blendedAccent = new THREE.Color();
  private readonly targetAccent = new THREE.Color();
  private readonly eventDisposers: Array<() => void> = [];

  private playerXMeters = 0;
  private playerZMeters = 0;
  private velocityXMeters = 0;
  private velocityZMeters = 0;
  private activeTierIndex = 0;
  private renderMetersPerUnit = getTier(0).metersPerUnit;
  private transition: TransitionState | null = null;
  private prewarmedTierIndex: number | null = null;
  private queuedTransitionTierIndex: number | null = null;
  private running = false;
  private paused = false;
  private visibilityPaused = false;
  private finalePaused = false;
  private completedShown = false;
  private goalAbsorbed = false;
  private goalAbsorbing = false;
  private goalAbsorbElapsedSeconds = 0;
  private goalWarningSeconds = 0;
  private navigationTargetXMeters: number | null = null;
  private navigationTargetZMeters: number | null = null;
  private navigationTargetName = "";
  private lastObjectiveAnnouncement = "";
  private autoplay = false;
  private lastTimeMilliseconds = 0;
  private accumulatorSeconds = 0;
  private elapsedSeconds = 0;
  private qualitySampleSeconds = 0;
  private uiSampleSeconds = 0;
  private collisionCandidates = 0;
  private toastTimer = 0;
  private ready = false;
  private readonly adaptiveQualityEnabled: boolean;
  private runStats = new RunStats();
  private runResult: Readonly<RunResult> | null = null;
  private lastMovementInput: MovementInput = { x: 0, z: 0, boost: false, dash: false };
  private surfaceHeightMeters = 0;
  private homeExited = false;

  constructor() {
    this.canvas = mustElement<HTMLCanvasElement>("game-canvas");
    this.ui = this.collectUi();
    const params = new URLSearchParams(location.search);
    this.adaptiveQualityEnabled = params.get("adaptive") !== "0";
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: false,
      powerPreference: "high-performance",
      stencil: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.22;

    const requestedQuality = params.get("quality")?.toUpperCase();
    const initialQuality: QualityLevel = requestedQuality === "ECO" || requestedQuality === "BALANCED"
      ? requestedQuality
      : "ULTRA";
    this.quality = new QualityController(initialQuality);
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, this.quality.settings.pixelRatioCap));

    this.environment = new Environment(this.scene);
    this.scene.add(this.environment.group);

    this.world = new WorldManager(
      this.activeTierIndex,
      this.quality.settings.density,
      this.playerXMeters,
      this.playerZMeters,
    );
    this.clearInitialCameraCorridor();
    this.scene.add(this.world.group);

    const playerVisual = this.createPlayerVisual();
    this.ballCore = playerVisual.core;
    this.ballWire = playerVisual.wire;
    this.ballInner = playerVisual.inner;
    this.shadow = playerVisual.shadow;
    this.scene.add(this.shadow, this.ballRoot, this.particles.points, this.goalLandmark.group);

    this.input = new InputController(
      this.canvas,
      this.ui.joystick,
      this.ui.joystickKnob,
      this.ui.boostButton,
    );
    this.setupLighting();
    this.bindEvents();
    this.applyQuality(this.quality.settings);
    this.resize();

    this.autoplay = params.get("autoplay") === "1";
    const showTelemetry = params.has("debug") || params.has("perf") || import.meta.env.DEV;
    this.ui.telemetry.classList.toggle("visible", showTelemetry);
    this.ui.telemetry.setAttribute("aria-hidden", String(!showTelemetry));
    if (matchMedia("(hover: none), (pointer: coarse)").matches) {
      this.ui.touchControls.classList.add("active");
      this.ui.touchControls.setAttribute("aria-hidden", "false");
    }

    this.camera.position.set(3.4, 3.1, 3.4);
    this.updateVisuals(0);
    this.prewarmGoalLandmark();
    this.updateUi();
    this.ready = true;
    this.ui.app.dataset.ready = "true";
    this.installDebugApi(params.has("debug") || this.autoplay || import.meta.env.DEV);
    this.publishSnapshot();
    this.renderer.setAnimationLoop(this.animate);

    if (this.autoplay) {
      this.running = true;
      this.ui.intro.classList.add("hidden");
    }
  }

  snapshot(): GameSnapshot {
    const visualTierIndex = this.visualTierIndex;
    const tier = getTier(visualTierIndex);
    const worldMetrics = this.world.metrics();
    const timing = this.metrics.summary();
    const radiusMeters = this.growth.radiusMeters;
    const objective = this.getObjectiveState();
    const navigation = this.getNavigationMetrics();
    const run = this.runStats.snapshot();
    const dash = this.dash.snapshot();
    return {
      schemaVersion: "2.0.0",
      ready: this.ready,
      running: this.running,
      paused: this.paused || this.visibilityPaused || this.finalePaused,
      tierIndex: visualTierIndex,
      tierName: tier.name,
      radiusMeters,
      diameterMeters: radiusMeters * 2,
      localRadius: radiusMeters / this.renderMetersPerUnit,
      renderMetersPerUnit: this.renderMetersPerUnit,
      viewRadiusMeters: this.renderMetersPerUnit * 95,
      positionMeters: { x: this.playerXMeters, z: this.playerZMeters },
      cameraYawRadians: this.input.cameraYaw,
      velocityMetersPerSecond: Math.hypot(this.velocityXMeters, this.velocityZMeters),
      pickupCount: this.growth.pickupCount,
      score: run.score,
      runTimeSeconds: run.timeSeconds,
      rank: this.runResult?.rank ?? null,
      rareCount: run.rareCount,
      combo: run.combo,
      comboMultiplier: run.comboMultiplier,
      dashActive: dash.active,
      dashReady: dash.ready,
      dashCharge: dash.charge01,
      surfaceHeightMeters: this.surfaceHeightMeters,
      homeExited: this.homeExited,
      audioVoices: this.audio.voiceCount,
      audioMusicActive: this.audio.musicActive,
      shareUrl: this.runResult ? buildResultShareUrl(this.runResult, location.href) : null,
      completed: this.goalAbsorbed,
      objectivePhase: objective.phase,
      objectiveTitle: objective.title,
      navigationDistanceMeters: navigation?.distanceMeters ?? null,
      navigationBearingRadians: navigation?.bearingRadians ?? null,
      goalVisible: this.goalLandmark.group.visible,
      goalReady: this.isGoalReady,
      goalAbsorbing: this.goalAbsorbing,
      goalAbsorbed: this.goalAbsorbed,
      goalRadiusMeters: GOAL_OBJECT_RADIUS_METERS,
      goalRequiredRadiusMeters: GOAL_REQUIRED_RADIUS_METERS,
      goalPositionMeters: this.goalLandmark.spawned
        ? { x: this.goalLandmark.position.x, z: this.goalLandmark.position.z }
        : null,
      transitioning: Boolean(this.transition),
      transitionProgress: this.transition?.progress ?? 0,
      activeInstances: worldMetrics.activeInstances,
      allocatedSlots: worldMetrics.allocatedSlots,
      gpuInstanceCapacity: worldMetrics.gpuInstanceCapacity,
      maximumSteadyInstances: MAX_WORLD_INSTANCES,
      attachedInstances: this.attachmentCloud.count,
      maximumAttachedInstances: MAX_ATTACHED_INSTANCES,
      collisionCandidates: this.collisionCandidates,
      drawCalls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
      geometries: this.renderer.info.memory.geometries,
      textures: this.renderer.info.memory.textures,
      fps: timing.fps,
      frameAverageMs: timing.frameAverageMs,
      frameP95Ms: timing.frameP95Ms,
      frameP99Ms: timing.frameP99Ms,
      quality: this.quality.settings.level,
      autoplay: this.autoplay,
      stateHash: hashNumbers(
        Math.round(radiusMeters * 1000),
        Math.round(this.playerXMeters * 10),
        Math.round(this.playerZMeters * 10),
        this.growth.pickupCount,
        run.score,
        run.rareCount,
        Math.round(run.timeSeconds * 10),
        dash.active ? 1 : 0,
        visualTierIndex,
        this.goalAbsorbed ? 1 : this.goalAbsorbing ? 2 : 0,
      ),
      seed: WORLD_SEED,
    };
  }

  start(): void {
    void this.audio.initialize().then(() => {
      this.audio.setPaused(false);
      this.audio.setTier(this.activeTierIndex);
    }).catch(() => undefined);
    this.running = true;
    this.paused = false;
    this.ui.intro.classList.add("hidden");
    this.ui.pauseScreen.classList.remove("visible");
    this.ui.pauseScreen.setAttribute("aria-hidden", "true");
    this.lastTimeMilliseconds = performance.now();
    this.canvas.focus({ preventScroll: true });
  }

  restart(): void {
    this.input.reset(true);
    this.growth.reset();
    this.runStats = new RunStats();
    this.runResult = null;
    this.dash.reset();
    this.playerXMeters = 0;
    this.playerZMeters = 0;
    this.velocityXMeters = 0;
    this.velocityZMeters = 0;
    this.activeTierIndex = 0;
    this.renderMetersPerUnit = getTier(0).metersPerUnit;
    this.transition = null;
    this.prewarmedTierIndex = null;
    this.queuedTransitionTierIndex = null;
    this.completedShown = false;
    this.goalAbsorbed = false;
    this.goalAbsorbing = false;
    this.goalAbsorbElapsedSeconds = 0;
    this.goalWarningSeconds = 0;
    this.surfaceHeightMeters = terrainHeightMeters(0, 0, 0);
    this.homeExited = false;
    this.lastMovementInput = { x: 0, z: 0, boost: false, dash: false };
    this.navigationTargetXMeters = null;
    this.navigationTargetZMeters = null;
    this.navigationTargetName = "";
    this.lastObjectiveAnnouncement = "";
    this.finalePaused = false;
    this.ballRoot.quaternion.identity();
    this.attachmentCloud.clear();
    this.particles.clear();
    this.goalLandmark.reset();
    this.world.reset(0, 0, 0);
    this.clearInitialCameraCorridor();
    this.metrics.reset();
    this.ui.finaleScreen.classList.remove("visible");
    this.ui.finaleScreen.setAttribute("aria-hidden", "true");
    this.ui.shiftOverlay.classList.remove("visible");
    this.ui.pickupToast.classList.remove("visible");
    this.ui.pickupToast.setAttribute("aria-hidden", "true");
    this.paused = false;
    this.running = true;
    this.accumulatorSeconds = 0;
    this.lastTimeMilliseconds = performance.now();
    this.elapsedSeconds = 0;
    this.audio.setTier(0);
    this.audio.resetTransientSounds();
    this.audio.setPaused(false);
    this.updateUi();
  }

  dispose(): void {
    this.renderer.setAnimationLoop(null);
    this.input.dispose();
    this.world.dispose();
    this.environment.dispose();
    this.goalLandmark.dispose();
    this.attachmentCloud.dispose();
    this.particles.dispose();
    this.audio.dispose();
    this.ballCore.geometry.dispose();
    (this.ballCore.material as THREE.Material).dispose();
    this.ballWire.geometry.dispose();
    (this.ballWire.material as THREE.Material).dispose();
    this.ballInner.geometry.dispose();
    (this.ballInner.material as THREE.Material).dispose();
    this.shadow.geometry.dispose();
    (this.shadow.material as THREE.Material).dispose();
    this.renderer.dispose();
    for (const dispose of this.eventDisposers) {
      dispose();
    }
  }

  private readonly animate = (timeMilliseconds: number): void => {
    const timing = frameTiming(this.lastTimeMilliseconds, timeMilliseconds);
    const deltaSeconds = timing.simulationSeconds;
    this.lastTimeMilliseconds = timeMilliseconds;
    this.elapsedSeconds += timing.activeWallSeconds;
    this.metrics.addFrame(timing.activeWallSeconds);

    if (this.running && !this.paused && !this.visibilityPaused && !this.finalePaused) {
      // The competitive clock is foreground wall time. Physics remains bounded
      // to three fixed steps so a slow frame cannot create a spiral of death.
      this.runStats.advance(timing.activeWallSeconds);
      this.accumulatorSeconds += deltaSeconds;
      let stepCount = 0;
      while (
        this.accumulatorSeconds >= FIXED_STEP_SECONDS
        && stepCount < MAX_CATCH_UP_STEPS
      ) {
        this.fixedUpdate(FIXED_STEP_SECONDS, false);
        this.accumulatorSeconds -= FIXED_STEP_SECONDS;
        stepCount += 1;
      }
      if (stepCount === MAX_CATCH_UP_STEPS) {
        this.accumulatorSeconds = 0;
      }
    }

    this.updateVisuals(deltaSeconds);
    this.renderer.render(this.scene, this.camera);

    this.uiSampleSeconds += timing.activeWallSeconds;
    if (this.uiSampleSeconds >= 0.1) {
      this.uiSampleSeconds = 0;
      this.updateUi();
      this.publishSnapshot();
    }
    if (this.running && this.elapsedSeconds > 5 && this.adaptiveQualityEnabled) {
      this.sampleQuality(timing.activeWallSeconds);
    }
  };

  private fixedUpdate(deltaSeconds: number, advanceRunClock = true): void {
    this.updateTransition(deltaSeconds);
    const input = this.goalAbsorbing
      ? { x: 0, z: 0, boost: false, dash: false }
      : this.autoplay
        ? this.readAutoplayInput()
        : this.input.read();
    this.lastMovementInput = input;
    if (advanceRunClock) this.runStats.advance(deltaSeconds);
    const dash = this.dash.update(deltaSeconds, input.dash);
    if (dash.triggered) this.audio.dash();
    if (dash.becameReady) this.audio.dashReady();
    const direction = this.worldDirection(input);
    const directionLength = Math.hypot(direction.x, direction.z);
    const boost = input.boost ? 1.55 : 1;
    const maximumSpeed = this.renderMetersPerUnit * 8.6 * boost * dash.speedCapMultiplier;
    const acceleration = this.renderMetersPerUnit * 42 * (dash.active ? 1.8 : 1);

    if (directionLength > 0.01) {
      this.velocityXMeters += direction.x * acceleration * deltaSeconds;
      this.velocityZMeters += direction.z * acceleration * deltaSeconds;
    } else {
      const damping = Math.exp(-5.4 * deltaSeconds);
      this.velocityXMeters *= damping;
      this.velocityZMeters *= damping;
    }

    const speed = Math.hypot(this.velocityXMeters, this.velocityZMeters);
    if (speed > maximumSpeed) {
      const ratio = maximumSpeed / speed;
      this.velocityXMeters *= ratio;
      this.velocityZMeters *= ratio;
    }

    const proposedXMeters = this.playerXMeters + this.velocityXMeters * deltaSeconds;
    const proposedZMeters = this.playerZMeters + this.velocityZMeters * deltaSeconds;
    if (this.activeTierIndex === 0 && !this.homeExited) {
      const wasInsideHome = !this.homeExited;
      const homeMovement = constrainInitialHomeMovement(
        proposedXMeters,
        proposedZMeters,
        this.growth.radiusMeters,
        this.growth.pickupCount >= HOME_TUTORIAL_PICKUPS,
      );
      this.playerXMeters = homeMovement.xMeters;
      this.playerZMeters = homeMovement.zMeters;
      if (homeMovement.blocked) {
        if (homeMovement.xMeters !== proposedXMeters) this.velocityXMeters = 0;
        if (homeMovement.zMeters !== proposedZMeters) this.velocityZMeters = 0;
      }
      this.homeExited = homeMovement.exited;
      if (
        wasInsideHome
        && this.homeExited
        && this.growth.tierIndex !== this.activeTierIndex
        && !this.transition
      ) {
        this.beginTierTransition(this.growth.tierIndex);
      }
    } else {
      this.playerXMeters = proposedXMeters;
      this.playerZMeters = proposedZMeters;
      if (this.activeTierIndex > 0) this.homeExited = true;
    }
    this.surfaceHeightMeters = terrainHeightMeters(
      this.playerXMeters,
      this.playerZMeters,
      this.visualTierIndex,
    );
    this.world.updateStreaming(this.playerXMeters, this.playerZMeters);
    if (this.goalAbsorbing) {
      this.updateGoalAbsorption(deltaSeconds);
    } else {
      this.resolveCollisions();
      this.resolveGoalCollision();
    }
    this.updateRolling(deltaSeconds);
    this.particles.update(deltaSeconds);

    if (this.toastTimer > 0) {
      this.toastTimer -= deltaSeconds;
      if (this.toastTimer <= 0) {
        this.ui.pickupToast.classList.remove("visible");
        this.ui.pickupToast.setAttribute("aria-hidden", "true");
      }
    }
    if (this.goalWarningSeconds > 0) {
      this.goalWarningSeconds = Math.max(0, this.goalWarningSeconds - deltaSeconds);
    }
  }

  private resolveCollisions(): void {
    const radiusMeters = this.growth.radiusMeters;
    const tier = getTier(this.world.collisionTierIndex);
    const candidates = this.world.queryNearby(
      this.playerXMeters,
      this.playerZMeters,
      radiusMeters + tier.metersPerUnit * 2.35,
    );
    this.collisionCandidates = candidates.length;
    let repelled = false;

    for (const item of candidates) {
      const collisionRadius = radiusMeters + item.radiusMeters * 0.58;
      const distanceSquared = squaredDistance(
        this.playerXMeters,
        this.playerZMeters,
        item.xMeters,
        item.zMeters,
      );
      if (distanceSquared > collisionRadius * collisionRadius) {
        continue;
      }
      if (this.growth.canAbsorb(item.radiusMeters)) {
        // Six pickups unlock the front door. Further room objects stay passive
        // until the player actually crosses the genkan, so growth cannot erase
        // the house or bypass the tutorial transition while still indoors.
        if (!this.homeExited && this.growth.pickupCount >= HOME_TUTORIAL_PICKUPS) {
          continue;
        }
        const result = this.growth.absorb(item.radiusMeters);
        if (result.accepted) {
          this.world.collect(item);
          this.handlePickup(item, result);
        }
      } else if (!repelled) {
        const distance = Math.max(0.001, Math.sqrt(distanceSquared));
        const normalX = (this.playerXMeters - item.xMeters) / distance;
        const normalZ = (this.playerZMeters - item.zMeters) / distance;
        const penetration = collisionRadius - distance;
        this.playerXMeters += normalX * Math.min(penetration, tier.metersPerUnit * 0.16);
        this.playerZMeters += normalZ * Math.min(penetration, tier.metersPerUnit * 0.16);
        const towardObstacle = this.velocityXMeters * -normalX + this.velocityZMeters * -normalZ;
        if (towardObstacle > 0) {
          this.velocityXMeters += normalX * towardObstacle * 0.92;
          this.velocityZMeters += normalZ * towardObstacle * 0.92;
        }
        repelled = true;
      }
    }
  }

  private resolveGoalCollision(): void {
    if (
      !this.goalLandmark.spawned
      || this.goalLandmark.absorbed
      || this.transition
      || this.activeTierIndex !== TIERS.length - 1
    ) {
      return;
    }

    const distance = this.goalLandmark.distanceTo(this.playerXMeters, this.playerZMeters);
    const collisionRadius = this.growth.radiusMeters + GOAL_OBJECT_RADIUS_METERS * 0.58;
    if (distance > collisionRadius) {
      return;
    }

    if (this.isGoalReady) {
      this.beginGoalAbsorption();
      return;
    }

    const safeDistance = Math.max(0.001, distance);
    let normalX = (this.playerXMeters - this.goalLandmark.position.x) / safeDistance;
    let normalZ = (this.playerZMeters - this.goalLandmark.position.z) / safeDistance;
    if (Math.abs(normalX) + Math.abs(normalZ) < 0.001) {
      normalX = Math.sin(this.input.cameraYaw);
      normalZ = Math.cos(this.input.cameraYaw);
    }
    const penetration = collisionRadius - distance;
    const clearance = getTier(TIERS.length - 1).metersPerUnit * 0.035;
    this.playerXMeters += normalX * (penetration + clearance);
    this.playerZMeters += normalZ * (penetration + clearance);
    this.velocityXMeters = 0;
    this.velocityZMeters = 0;
    this.goalWarningSeconds = 1.8;
  }

  private beginGoalAbsorption(): void {
    if (this.goalAbsorbing || this.goalAbsorbed || !this.isGoalReady) {
      return;
    }
    const result = this.growth.absorb(GOAL_OBJECT_RADIUS_METERS);
    if (!result.accepted) {
      return;
    }
    const goalItem: WorldItem = {
      id: hashNumbers(WORLD_SEED, 0x50_41_4c),
      chunkX: 0,
      chunkZ: 0,
      ordinal: 0,
      xMeters: this.goalLandmark.position.x,
      zMeters: this.goalLandmark.position.z,
      surfaceYMeters: terrainHeightMeters(
        this.goalLandmark.position.x,
        this.goalLandmark.position.z,
        TIERS.length - 1,
      ),
      radiusMeters: GOAL_OBJECT_RADIUS_METERS,
      heightScale: 3.8,
      archetype: 3,
      colorIndex: 0,
      rotation: 0,
      name: GOAL_OBJECT_NAME,
      rare: false,
      rareId: null,
      active: false,
      renderSlot: -1,
    };
    this.goalAbsorbing = true;
    this.goalAbsorbElapsedSeconds = 0;
    this.velocityXMeters = 0;
    this.velocityZMeters = 0;
    this.handlePickup(goalItem, result);
    this.runResult = this.runStats.finish();
    this.audio.goal();
  }

  private updateGoalAbsorption(deltaSeconds: number): void {
    this.goalAbsorbElapsedSeconds += deltaSeconds;
    if (this.goalAbsorbElapsedSeconds < 0.95) {
      return;
    }
    this.goalAbsorbing = false;
    this.goalAbsorbed = true;
    this.goalLandmark.markAbsorbed();
    this.showFinale();
  }

  private handlePickup(item: WorldItem, result: AbsorbResult): void {
    const tier = getTier(result.tierIndex);
    const relativeSize = item.radiusMeters / Math.max(0.001, result.radiusBeforeMeters);
    const score = this.runStats.finished
      ? { delta: result.points }
      : this.runStats.scorePickup(result.points, item.rare);
    this.attachmentCloud.add(item.id, item.radiusMeters, tier.accent);
    this.particles.emit(item.id, result.radiusMeters / this.renderMetersPerUnit, tier.accent);
    this.audio.pickup(relativeSize, result.tierIndex);
    if (item.rare) this.audio.rare();
    this.showPickupToast(item, score.delta);

    if (
      result.tierChanged
      && result.tierIndex !== this.activeTierIndex
      && (this.activeTierIndex !== 0 || this.homeExited)
    ) {
      this.beginTierTransition(result.tierIndex);
    } else {
      this.maybePrewarmNextTier();
    }
  }

  private beginTierTransition(targetTierIndex: number): void {
    const boundedTarget = clamp(Math.floor(targetTierIndex), 0, TIERS.length - 1);
    if (boundedTarget === this.activeTierIndex && !this.transition) {
      return;
    }
    if (this.transition) {
      if (this.transition.toTierIndex !== boundedTarget) {
        this.queuedTransitionTierIndex = boundedTarget;
      }
      return;
    }
    this.startTierTransition(boundedTarget);
  }

  private startTierTransition(boundedTarget: number): void {
    const wasPrewarmed = this.prewarmedTierIndex === boundedTarget;
    this.world.beginTransition(
      boundedTarget,
      this.playerXMeters,
      this.playerZMeters,
    );
    this.transition = {
      fromTierIndex: this.activeTierIndex,
      toTierIndex: boundedTarget,
      fromScale: this.renderMetersPerUnit,
      elapsedSeconds: 0,
      progress: 0,
    };
    if (boundedTarget === TIERS.length - 1 && !this.goalLandmark.spawned) {
      const goalDx = SKYTREE_X_METERS - this.playerXMeters;
      const goalDz = SKYTREE_Z_METERS - this.playerZMeters;
      this.goalLandmark.spawn(
        this.playerXMeters,
        this.playerZMeters,
        Math.atan2(-goalDx, -goalDz),
        Math.hypot(goalDx, goalDz),
      );
      // Pin the landmark to the Tokyo map, independent of camera direction.
      this.goalLandmark.position.x = SKYTREE_X_METERS;
      this.goalLandmark.position.z = SKYTREE_Z_METERS;
      this.goalLandmark.position.y = terrainHeightMeters(
        SKYTREE_X_METERS,
        SKYTREE_Z_METERS,
        boundedTarget,
      );
      this.world.clearArea(
        boundedTarget,
        this.goalLandmark.position.x,
        this.goalLandmark.position.z,
        GOAL_OBJECT_RADIUS_METERS * 2.75,
      );
    }
    this.prewarmedTierIndex = null;
    this.queuedTransitionTierIndex = null;
    const targetTier = getTier(boundedTarget);
    this.ui.shiftName.textContent = targetTier.name;
    const ratio = targetTier.metersPerUnit / Math.max(0.001, getTier(this.activeTierIndex).metersPerUnit);
    this.ui.shiftScale.textContent = `VIEW SCALE ×${ratio.toFixed(0)}`;
    this.ui.shiftOverlay.classList.add("visible");
    this.ui.shiftOverlay.setAttribute("aria-hidden", "false");
    this.audio.shift(boundedTarget);
    if (!wasPrewarmed && this.world.pendingTierIndex === boundedTarget) {
      this.compilePendingWorld();
    }
  }

  private maybePrewarmNextTier(): void {
    if (
      this.transition
      || this.activeTierIndex >= TIERS.length - 1
      || this.growth.tierIndex !== this.activeTierIndex
      || tierProgress(this.growth.radiusMeters, this.activeTierIndex) < 0.78
    ) {
      return;
    }
    const targetTierIndex = this.activeTierIndex + 1;
    if (this.prewarmedTierIndex === targetTierIndex) {
      return;
    }
    this.world.prepareTransition(
      targetTierIndex,
      this.playerXMeters,
      this.playerZMeters,
    );
    this.prewarmedTierIndex = targetTierIndex;
    this.world.setPendingCompileVisible(true);
    this.compilePendingWorld();
    if (!this.transition) {
      this.world.setPendingCompileVisible(false);
    }
  }

  private compilePendingWorld(): void {
    const root = this.world.pendingCompileRoot;
    if (!root) {
      return;
    }
    try {
      // Three r185's compileAsync poller assumes every material keeps a
      // currentProgram until its next timeout. Transition opacity changes can
      // invalidate that program, so synchronously precompile only the bounded
      // five-material pending layer while it is still lifecycle-stable.
      this.renderer.compile(root, this.camera, this.scene);
    } catch (error) {
      console.warn("SOL ROLLER: optional shader precompile skipped", error);
    }
  }

  private updateTransition(deltaSeconds: number): void {
    if (!this.transition) {
      return;
    }
    this.transition.elapsedSeconds += deltaSeconds;
    this.transition.progress = clamp(
      this.transition.elapsedSeconds / TRANSITION_DURATION_SECONDS,
      0,
      1,
    );
    const eased = smoothstep(0, 1, this.transition.progress);
    const targetScale = getTier(this.transition.toTierIndex).metersPerUnit;
    this.renderMetersPerUnit = logLerp(this.transition.fromScale, targetScale, eased);
    this.world.setTransitionProgress(eased);

    if (this.transition.progress >= 1) {
      this.activeTierIndex = this.transition.toTierIndex;
      this.renderMetersPerUnit = targetScale;
      this.world.finishTransition();
      this.transition = null;
      const queuedTier = this.queuedTransitionTierIndex;
      if (queuedTier !== null && queuedTier !== this.activeTierIndex) {
        this.startTierTransition(queuedTier);
        return;
      }
      this.ui.shiftOverlay.classList.remove("visible");
      this.ui.shiftOverlay.setAttribute("aria-hidden", "true");
    }
  }

  private updateRolling(deltaSeconds: number): void {
    const speed = Math.hypot(this.velocityXMeters, this.velocityZMeters);
    if (speed < 0.0001) {
      return;
    }
    this.rollAxis.set(this.velocityZMeters, 0, -this.velocityXMeters).normalize();
    const rotation = (speed * deltaSeconds) / Math.max(0.01, this.growth.radiusMeters);
    this.ballRoot.rotateOnWorldAxis(this.rollAxis, rotation);
  }

  private updateVisuals(deltaSeconds: number): void {
    const radiusLocal = this.growth.radiusMeters / this.renderMetersPerUnit;
    // Rendering uses a floating Y origin at the player's sampled terrain.
    // The physical height remains in the snapshot while the ball stays on the
    // local ground plane, avoiding precision loss at later Tokyo scales.
    this.ballRoot.position.y = radiusLocal;
    this.ballCore.scale.setScalar(radiusLocal);
    this.ballWire.scale.setScalar(radiusLocal * 1.015);
    this.attachmentCloud.update(radiusLocal, this.renderMetersPerUnit);
    this.shadow.scale.set(radiusLocal * 1.38, radiusLocal * 1.38, 1);
    this.shadow.position.y = 0.005;
    (this.shadow.material as THREE.MeshBasicMaterial).opacity = clamp(0.3 - radiusLocal * 0.018, 0.12, 0.28);
    this.world.updateMatrices(
      this.playerXMeters,
      this.playerZMeters,
      this.renderMetersPerUnit,
      this.elapsedSeconds,
    );
    const goalRevealVisible =
      this.activeTierIndex === TIERS.length - 1
      || (this.transition?.toTierIndex === TIERS.length - 1
        && this.transition.progress >= 0.22);
    this.goalLandmark.update(
      this.playerXMeters,
      this.playerZMeters,
      this.renderMetersPerUnit,
      this.elapsedSeconds,
      goalRevealVisible && !this.goalAbsorbed,
      this.goalAbsorbing ? this.goalAbsorbElapsedSeconds / 0.95 : 0,
    );

    const fromTier = getTier(this.transition?.fromTierIndex ?? this.activeTierIndex);
    const toTier = getTier(this.transition?.toTierIndex ?? this.activeTierIndex);
    const blend = smoothstep(0, 1, this.transition?.progress ?? 0);
    this.environment.update(
      this.playerXMeters,
      this.playerZMeters,
      this.renderMetersPerUnit,
      this.elapsedSeconds,
      fromTier,
      toTier,
      blend,
    );
    this.blendedAccent
      .set(fromTier.accent)
      .lerp(this.targetAccent.set(toTier.accent), blend);
    (this.ballWire.material as THREE.MeshBasicMaterial).color.copy(this.blendedAccent);

    // Keep the title camera on the corridor cleared at construction time. A
    // free-running orbit could eventually place deterministic room items
    // between the camera and the ball before the player even starts.
    const yaw = this.input.cameraYaw;
    const cameraDistance = Math.max(4.4, radiusLocal * 5.5);
    const cameraHeight = Math.max(3, radiusLocal * 3.75);
    this.desiredCamera.set(
      Math.sin(yaw) * cameraDistance,
      cameraHeight + radiusLocal * 0.18,
      Math.cos(yaw) * cameraDistance,
    );
    const cameraSmoothing = this.transition ? 20 : 7.5;
    this.camera.position.x = damp(
      this.camera.position.x,
      this.desiredCamera.x,
      cameraSmoothing,
      deltaSeconds,
    );
    this.camera.position.y = damp(
      this.camera.position.y,
      this.desiredCamera.y,
      cameraSmoothing,
      deltaSeconds,
    );
    this.camera.position.z = damp(
      this.camera.position.z,
      this.desiredCamera.z,
      cameraSmoothing,
      deltaSeconds,
    );
    this.cameraTarget.set(0, radiusLocal * 0.38, 0);
    this.camera.lookAt(this.cameraTarget);
    const targetFov = 51
      + (this.lastMovementInput.boost ? 1.8 : 0)
      + (this.dash.active ? 3.5 : 0);
    this.camera.fov = damp(this.camera.fov, targetFov, 8, deltaSeconds);
    this.camera.updateProjectionMatrix();
  }

  private worldDirection(input: MovementInput): { x: number; z: number } {
    if (this.autoplay) {
      const target = this.world.findNearestEligible(
        this.playerXMeters,
        this.playerZMeters,
        this.growth.radiusMeters * PICKUP_RADIUS_RATIO,
      );
      if (target) {
        const dx = target.xMeters - this.playerXMeters;
        const dz = target.zMeters - this.playerZMeters;
        const length = Math.max(0.001, Math.hypot(dx, dz));
        return { x: dx / length, z: dz / length };
      }
    }
    const sin = Math.sin(this.input.cameraYaw);
    const cos = Math.cos(this.input.cameraYaw);
    return {
      x: input.x * cos - input.z * sin,
      z: -input.x * sin - input.z * cos,
    };
  }

  private readAutoplayInput(): MovementInput {
    return { x: 0, z: 1, boost: true, dash: false };
  }

  private showPickupToast(item: WorldItem, points: number): void {
    const icons = ["◆", "■", "▲", "⬢", "●"];
    this.ui.pickupIcon.textContent = item.rare ? "★" : icons[item.archetype] ?? "◆";
    this.ui.pickupName.textContent = item.name.toUpperCase();
    this.ui.pickupPoints.textContent = `+${points}`;
    this.ui.pickupToast.classList.toggle("rare", item.rare);
    this.ui.pickupToast.classList.add("visible");
    this.ui.pickupToast.setAttribute("aria-hidden", "false");
    this.toastTimer = 1.15;
  }

  private showFinale(): void {
    if (!this.goalAbsorbed || this.completedShown) {
      return;
    }
    this.completedShown = true;
    this.finalePaused = true;
    this.audio.setPaused(true, true);
    this.runResult = this.runResult ?? this.runStats.finish();
    const result = this.runResult;
    const size = formatPhysicalSize(this.growth.radiusMeters * 2);
    this.ui.finaleRank.textContent = result.rank;
    this.ui.finaleTime.textContent = formatResultTime(result.timeSeconds);
    this.ui.finaleSize.textContent = `${size.value} ${size.unit}`;
    this.ui.finalePickups.textContent = this.growth.pickupCount.toString().padStart(4, "0");
    this.ui.finaleRares.textContent = result.rareCount.toString();
    this.ui.finaleTimeBonus.textContent = `+${result.timeBonus.toString().padStart(5, "0")}`;
    this.ui.finaleScore.textContent = result.score.toString().padStart(6, "0");
    this.ui.shareButton.href = buildResultShareUrl(result, location.href);
    this.audio.result(result.rank);
    try {
      const previous = JSON.parse(localStorage.getItem("solKatamari.v2.best") ?? "null") as {
        timeSeconds?: number;
        score?: number;
      } | null;
      const record = {
        ruleset: result.ruleset,
        timeSeconds: Math.min(previous?.timeSeconds ?? Number.POSITIVE_INFINITY, result.timeSeconds),
        score: Math.max(previous?.score ?? 0, result.score),
      };
      localStorage.setItem("solKatamari.v2.best", JSON.stringify(record));
    } catch {
      // Private browsing/quota failures never block the result screen.
    }
    this.ui.finaleScreen.classList.add("visible");
    this.ui.finaleScreen.setAttribute("aria-hidden", "false");
    requestAnimationFrame(() => this.ui.continueButton.focus({ preventScroll: true }));
  }

  private updateUi(): void {
    const radiusMeters = this.growth.radiusMeters;
    const tierIndex = this.visualTierIndex;
    const tier = getTier(tierIndex);
    const size = formatPhysicalSize(radiusMeters * 2);
    const objective = this.getObjectiveState();
    const progress = objective.progress;
    const timing = this.metrics.summary();
    const worldMetrics = this.world.metrics();
    const run = this.runStats.snapshot();
    const dash = this.dash.snapshot();
    this.selectNavigationTarget(objective.phase);
    const navigation = this.getNavigationMetrics();
    this.ui.sizeValue.textContent = size.value;
    this.ui.sizeUnit.textContent = size.unit;
    this.ui.tierIndex.textContent = (tierIndex + 1).toString().padStart(2, "0");
    this.ui.tierName.textContent = `${tier.name} · ${tier.nameJa}`;
    this.ui.progressFill.style.width = `${(progress * 100).toFixed(1)}%`;
    this.ui.progressValue.textContent = `${Math.round(progress * 100)}%`;
    this.ui.progressLabel.textContent = objective.progressLabel;
    this.ui.progressTrack.setAttribute("aria-valuenow", Math.round(progress * 100).toString());
    this.ui.objectiveStep.textContent = objective.step;
    this.ui.objectiveTitle.textContent = objective.title;
    this.ui.objectiveDetail.textContent = objective.detail;
    this.ui.navigationTarget.textContent = this.navigationTargetName || objective.targetLabel;
    this.ui.objectivePanel.classList.toggle("final-grow", objective.phase === "FINAL_GROW");
    this.ui.objectivePanel.classList.toggle("final-ready", objective.phase === "FINAL_READY");
    this.ui.objectivePanel.classList.toggle("absorbing", objective.phase === "ABSORBING");
    this.ui.objectivePanel.classList.toggle("clear", objective.phase === "CLEAR");
    this.ui.objectivePanel.classList.toggle("no-target", navigation === null);
    if (navigation) {
      const distance = formatPhysicalSize(navigation.distanceMeters);
      this.ui.navigationDistance.textContent = `${distance.value} ${distance.unit}`;
      this.ui.navigationArrow.style.setProperty("--nav-angle", `${navigation.bearingRadians}rad`);
      this.ui.objectivePanel.setAttribute(
        "aria-label",
        `${objective.step}。${objective.title}。${objective.detail}。目標${this.navigationTargetName}まで${distance.value}${distance.unit}、${this.navigationDirectionLabel(navigation.bearingRadians)}方向。`,
      );
    } else {
      this.ui.navigationDistance.textContent = "SEARCHING";
      this.ui.navigationArrow.style.setProperty("--nav-angle", "0rad");
      this.ui.objectivePanel.setAttribute(
        "aria-label",
        `${objective.step}。${objective.title}。${objective.detail}。次の目標を探索中。`,
      );
    }
    const announcementKey = `${objective.phase}:${objective.title}`;
    if (announcementKey !== this.lastObjectiveAnnouncement) {
      this.lastObjectiveAnnouncement = announcementKey;
      this.ui.objectiveAnnouncer.textContent = `${objective.step}。${objective.title}。${objective.detail}`;
    }
    this.ui.pickupCount.textContent = this.growth.pickupCount.toString().padStart(4, "0");
    this.ui.scoreValue.textContent = run.score.toString().padStart(6, "0");
    this.ui.timerValue.textContent = formatResultTime(run.timeSeconds).padStart(7, "0");
    this.ui.rareValue.textContent = run.rareCount.toString();
    this.ui.comboValue.textContent = `×${run.comboMultiplier.toFixed(1)}`;
    this.ui.dashFill.style.width = `${(dash.charge01 * 100).toFixed(1)}%`;
    this.ui.dashLabel.textContent = dash.active
      ? "DASHING"
      : dash.ready
        ? "READY"
        : `${Math.ceil((1 - dash.charge01) * 4)}s`;
    this.ui.dashPanel.classList.toggle("active", dash.active);
    this.ui.dashPanel.classList.toggle("ready", dash.ready);
    const dashTrack = this.ui.dashFill.parentElement;
    dashTrack?.setAttribute("aria-valuenow", Math.round(dash.charge01 * 100).toString());
    this.ui.fpsValue.textContent = Math.round(timing.fps).toString();
    this.ui.instanceValue.textContent = worldMetrics.activeInstances.toString();
    this.ui.drawValue.textContent = this.renderer.info.render.calls.toString();
    this.ui.qualityValue.textContent = this.quality.settings.level;
    document.documentElement.style.setProperty("--accent", colorCss(tier.accent));
    document.documentElement.style.setProperty("--accent-soft", `${colorCss(tier.accent)}29`);
  }

  private getObjectiveState(): {
    phase: ObjectivePhase;
    step: string;
    title: string;
    detail: string;
    targetLabel: string;
    progress: number;
    progressLabel: string;
  } {
    if (this.goalAbsorbed) {
      return {
        phase: "CLEAR",
        step: "MISSION COMPLETE",
        title: `${GOAL_OBJECT_NAME}を吸収した`,
        detail: "巨大建造物の巻き込みに成功",
        targetLabel: "COMPLETE",
        progress: 1,
        progressLabel: "COMPLETE",
      };
    }
    if (this.goalAbsorbing) {
      const progress = clamp(this.goalAbsorbElapsedSeconds / 0.95, 0, 1);
      return {
        phase: "ABSORBING",
        step: "FINAL MISSION · ABSORBING",
        title: `${GOAL_OBJECT_NAME}を巻き込み中`,
        detail: "巨大建造物を球体へ引き込んでいる",
        targetLabel: GOAL_OBJECT_NAME,
        progress,
        progressLabel: "ABSORBING",
      };
    }

    const tierIndex = this.visualTierIndex;
    if (tierIndex === 0 && this.growth.pickupCount < HOME_TUTORIAL_PICKUPS) {
      return {
        phase: "GROW",
        step: "MISSION 01 / 06 · HOME",
        title: "部屋の小物を6個巻き込もう",
        detail: `あと${Math.max(0, 6 - this.growth.pickupCount)}個。光る矢印の先へ転がる`,
        targetLabel: "ROOM PICKUP",
        progress: Math.min(1, this.growth.pickupCount / HOME_TUTORIAL_PICKUPS),
        progressLabel: "HOME TUTORIAL",
      };
    }
    if (tierIndex === 0 && !this.homeExited) {
      return {
        phase: "HOME_EXIT",
        step: "MISSION 01 / 06 · EXIT HOME",
        title: "玄関の段差を越えて東京へ",
        detail: "中央の矢印を追い、開いた玄関から外へ出る",
        targetLabel: "FRONT DOOR",
        progress: 0.92,
        progressLabel: "LEAVE HOME",
      };
    }
    if (tierIndex < TIERS.length - 1) {
      const nextTier = getTier(tierIndex + 1);
      const nextDiameter = formatPhysicalSize(nextTier.metersPerUnit * ENTER_RADIUS_UNITS * 2);
      return {
        phase: "GROW",
        step: `MISSION ${String(tierIndex + 1).padStart(2, "0")} / 06 · GROW`,
        title: tierIndex < 2 ? "住宅街を巻き込んで東京へ" : "東京の街をもっと大きく巻き込もう",
        detail: `直径 ${nextDiameter.value} ${nextDiameter.unit} で ${nextTier.nameJa} へ`,
        targetLabel: "NEXT PICKUP",
        progress: tierProgress(this.growth.radiusMeters, tierIndex),
        progressLabel: "NEXT SCALE",
      };
    }

    const finalTier = getTier(TIERS.length - 1);
    const finalEntryRadius = finalTier.metersPerUnit * ENTER_RADIUS_UNITS;
    const readiness = clamp(
      (this.growth.radiusMeters - finalEntryRadius)
        / Math.max(0.001, GOAL_REQUIRED_RADIUS_METERS - finalEntryRadius),
      0,
      1,
    );
    if (!this.isGoalReady) {
      const missing = formatPhysicalSize(GOAL_REQUIRED_RADIUS_METERS - this.growth.radiusMeters);
      return {
        phase: "FINAL_GROW",
        step: "MISSION 06 / 06 · SKYTREE PREP",
        title: this.goalWarningSeconds > 0
          ? "まだ大きすぎて巻き込めない"
          : "東京スカイツリーに挑む準備",
        detail: this.goalWarningSeconds > 0
          ? `半径をあと ${missing.value} ${missing.unit} 大きくしよう`
          : `${GOAL_OBJECT_NAME}は出現済み。小物を集めて解除`,
        targetLabel: "GROW TARGET",
        progress: readiness,
        progressLabel: "SKYTREE UNLOCK",
      };
    }
    return {
      phase: "FINAL_READY",
      step: "FINAL MISSION · SKYTREE READY",
      title: `${GOAL_OBJECT_NAME}を巻き込め`,
      detail: "矢印と距離を追って巨大建造物へ",
      targetLabel: GOAL_OBJECT_NAME,
      progress: 1,
      progressLabel: "TARGET READY",
    };
  }

  private selectNavigationTarget(phase: ObjectivePhase): void {
    if (phase === "HOME_EXIT") {
      this.navigationTargetXMeters = INITIAL_HOME_EXIT_METERS.x;
      this.navigationTargetZMeters = INITIAL_HOME_EXIT_METERS.z;
      this.navigationTargetName = "FRONT DOOR · 玄関";
      return;
    }
    if (
      (phase === "FINAL_READY" || phase === "ABSORBING" || phase === "CLEAR")
      && this.goalLandmark.spawned
    ) {
      this.navigationTargetXMeters = this.goalLandmark.position.x;
      this.navigationTargetZMeters = this.goalLandmark.position.z;
      this.navigationTargetName = GOAL_OBJECT_NAME;
      return;
    }
    const target = this.world.findNearestEligible(
      this.playerXMeters,
      this.playerZMeters,
      this.growth.radiusMeters * PICKUP_RADIUS_RATIO,
    );
    this.navigationTargetXMeters = target?.xMeters ?? null;
    this.navigationTargetZMeters = target?.zMeters ?? null;
    this.navigationTargetName = target?.name.toUpperCase() ?? "NEXT PICKUP";
  }

  private getNavigationMetrics(): {
    distanceMeters: number;
    bearingRadians: number;
  } | null {
    if (this.navigationTargetXMeters === null || this.navigationTargetZMeters === null) {
      return null;
    }
    return navigationMetrics(
      this.playerXMeters,
      this.playerZMeters,
      this.navigationTargetXMeters,
      this.navigationTargetZMeters,
      this.input.cameraYaw,
    );
  }

  private navigationDirectionLabel(bearingRadians: number): string {
    const directions = ["正面", "右前", "右", "右後ろ", "後ろ", "左後ろ", "左", "左前"];
    const normalized = (bearingRadians + Math.PI * 2) % (Math.PI * 2);
    const index = Math.round(normalized / (Math.PI / 4)) % directions.length;
    return directions[index] ?? "正面";
  }

  private sampleQuality(deltaSeconds: number): void {
    this.qualitySampleSeconds += deltaSeconds;
    if (this.qualitySampleSeconds < 2) {
      return;
    }
    const elapsed = this.qualitySampleSeconds;
    this.qualitySampleSeconds = 0;
    const changed = this.quality.sample(this.metrics.summary().fps, elapsed);
    if (changed) {
      this.applyQuality(changed);
    }
  }

  private applyQuality(settings: QualitySettings): void {
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, settings.pixelRatioCap));
    this.renderer.setSize(innerWidth, innerHeight, false);
    this.world.setDensity(settings.density, this.playerXMeters, this.playerZMeters);
    this.ui.qualityValue.textContent = settings.level;
  }

  private setupLighting(): void {
    const hemisphere = new THREE.HemisphereLight(0xd8efff, 0x59686d, 2.55);
    hemisphere.name = "hemisphere-light";
    const key = new THREE.DirectionalLight(0xfff4d6, 2.85);
    key.name = "solar-key-light";
    key.position.set(-7, 11, 5);
    const fill = new THREE.DirectionalLight(0xaed7ff, 1.05);
    fill.name = "camera-opposite-fill-light";
    fill.position.set(7, 5, -8);
    this.scene.add(hemisphere, key, fill);
  }

  private clearInitialCameraCorridor(): void {
    const unit = getTier(0).metersPerUnit;
    const cameraXMeters = 3.4 * unit;
    const cameraZMeters = 3.4 * unit;
    for (const progress of [0.35, 0.65, 1]) {
      this.world.clearArea(
        0,
        cameraXMeters * progress,
        cameraZMeters * progress,
        0.46,
      );
    }
  }

  private prewarmGoalLandmark(): void {
    this.goalLandmark.group.visible = true;
    try {
      this.renderer.compile(this.goalLandmark.group, this.camera, this.scene);
      this.renderer.render(this.scene, this.camera);
    } finally {
      this.goalLandmark.group.visible = false;
      this.renderer.info.reset();
    }
  }

  private createPlayerVisual(): {
    core: THREE.Mesh;
    wire: THREE.Mesh;
    inner: THREE.Mesh;
    shadow: THREE.Mesh;
  } {
    this.ballRoot.name = "sol-roller";
    const coreGeometry = new THREE.IcosahedronGeometry(1, 2);
    const coreMaterial = new THREE.MeshStandardMaterial({
      color: 0xddebd9,
      roughness: 0.35,
      metalness: 0.18,
      flatShading: true,
    });
    const core = new THREE.Mesh(coreGeometry, coreMaterial);
    core.name = "sol-core";

    const wireGeometry = new THREE.IcosahedronGeometry(1, 1);
    const wireMaterial = new THREE.MeshBasicMaterial({
      color: getTier(0).accent,
      wireframe: true,
      transparent: true,
      opacity: 0.42,
      depthWrite: false,
    });
    const wire = new THREE.Mesh(wireGeometry, wireMaterial);
    wire.name = "sol-energy-cage";

    const inner = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.44, 0),
      new THREE.MeshStandardMaterial({
        color: 0xfff3a0,
        emissive: 0xffbd33,
        emissiveIntensity: 0.7,
        roughness: 0.3,
      }),
    );
    inner.name = "sol-inner-light";
    this.ballRoot.add(core, wire, inner, this.attachmentCloud.mesh);

    const shadow = new THREE.Mesh(
      new THREE.CircleGeometry(1, 32),
      new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0.24,
        depthWrite: false,
      }),
    );
    shadow.name = "blob-shadow";
    shadow.rotation.x = -Math.PI * 0.5;
    shadow.position.y = 0.005;
    return { core, wire, inner, shadow };
  }

  private bindEvents(): void {
    const start = (): void => this.start();
    const pause = (): void => this.setPaused(!this.paused);
    const resume = (): void => this.setPaused(false);
    const sound = (): void => {
      this.audio.setEnabled(!this.audio.isEnabled);
      this.ui.soundButton.setAttribute("aria-pressed", String(this.audio.isEnabled));
      this.ui.soundButton.style.opacity = this.audio.isEnabled ? "1" : "0.48";
    };
    const continueGame = (): void => {
      this.ui.finaleScreen.classList.remove("visible");
      this.ui.finaleScreen.setAttribute("aria-hidden", "true");
      this.finalePaused = false;
      this.audio.setPaused(false);
      this.lastTimeMilliseconds = performance.now();
      this.canvas.focus({ preventScroll: true });
    };
    const restart = (): void => {
      this.restart();
      this.canvas.focus({ preventScroll: true });
    };
    const resize = (): void => this.resize();
    const visibility = (): void => {
      this.visibilityPaused = document.visibilityState !== "visible";
      this.audio.setPaused(this.visibilityPaused || this.paused || this.finalePaused);
      this.lastTimeMilliseconds = performance.now();
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.code === "Escape" && this.running) {
        this.setPaused(!this.paused);
      }
    };

    this.ui.startButton.addEventListener("click", start);
    this.ui.pauseButton.addEventListener("click", pause);
    this.ui.resumeButton.addEventListener("click", resume);
    this.ui.soundButton.addEventListener("click", sound);
    this.ui.continueButton.addEventListener("click", continueGame);
    this.ui.restartButton.addEventListener("click", restart);
    window.addEventListener("resize", resize);
    window.addEventListener("keydown", escape);
    document.addEventListener("visibilitychange", visibility);
    this.eventDisposers.push(
      () => this.ui.startButton.removeEventListener("click", start),
      () => this.ui.pauseButton.removeEventListener("click", pause),
      () => this.ui.resumeButton.removeEventListener("click", resume),
      () => this.ui.soundButton.removeEventListener("click", sound),
      () => this.ui.continueButton.removeEventListener("click", continueGame),
      () => this.ui.restartButton.removeEventListener("click", restart),
      () => window.removeEventListener("resize", resize),
      () => window.removeEventListener("keydown", escape),
      () => document.removeEventListener("visibilitychange", visibility),
    );
  }

  private setPaused(paused: boolean): void {
    if (!this.running) {
      return;
    }
    this.paused = paused;
    this.ui.pauseButton.setAttribute("aria-pressed", String(paused));
    this.ui.pauseScreen.classList.toggle("visible", paused);
    this.ui.pauseScreen.setAttribute("aria-hidden", String(!paused));
    this.lastTimeMilliseconds = performance.now();
    this.audio.setPaused(paused || this.visibilityPaused || this.finalePaused);
    if (paused) {
      requestAnimationFrame(() => this.ui.resumeButton.focus({ preventScroll: true }));
    } else {
      this.canvas.focus({ preventScroll: true });
    }
  }

  private resize(): void {
    const width = Math.max(1, innerWidth);
    const height = Math.max(1, innerHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  private collectUi(): UiElements {
    return {
      app: mustElement("app"),
      intro: mustElement("intro"),
      startButton: mustElement<HTMLButtonElement>("start-button"),
      pauseButton: mustElement<HTMLButtonElement>("pause-button"),
      pauseScreen: mustElement("pause-screen"),
      resumeButton: mustElement<HTMLButtonElement>("resume-button"),
      soundButton: mustElement<HTMLButtonElement>("sound-button"),
      sizeValue: mustElement("size-value"),
      sizeUnit: mustElement("size-unit"),
      tierIndex: mustElement("tier-index"),
      tierName: mustElement("tier-name"),
      progressTrack: mustElement("scale-progress"),
      progressFill: mustElement("progress-fill"),
      progressLabel: mustElement("progress-label"),
      progressValue: mustElement("progress-value"),
      pickupCount: mustElement("pickup-count"),
      scoreValue: mustElement("score-value"),
      timerValue: mustElement("timer-value"),
      rareValue: mustElement("rare-value"),
      comboValue: mustElement("combo-value"),
      dashPanel: mustElement("dash-panel"),
      dashFill: mustElement("dash-fill"),
      dashLabel: mustElement("dash-label"),
      fpsValue: mustElement("fps-value"),
      instanceValue: mustElement("instance-value"),
      drawValue: mustElement("draw-value"),
      qualityValue: mustElement("quality-value"),
      telemetry: mustElement("telemetry"),
      objectivePanel: mustElement("objective-panel"),
      objectiveStep: mustElement("objective-step"),
      objectiveTitle: mustElement("objective-title"),
      objectiveDetail: mustElement("objective-detail"),
      objectiveAnnouncer: mustElement("objective-announcer"),
      navigationArrow: mustElement("navigation-arrow"),
      navigationTarget: mustElement("navigation-target"),
      navigationDistance: mustElement("navigation-distance"),
      pickupToast: mustElement("pickup-toast"),
      pickupIcon: mustElement("pickup-icon"),
      pickupName: mustElement("pickup-name"),
      pickupPoints: mustElement("pickup-points"),
      shiftOverlay: mustElement("shift-overlay"),
      shiftName: mustElement("shift-name"),
      shiftScale: mustElement("shift-scale"),
      touchControls: mustElement("touch-controls"),
      joystick: mustElement("joystick"),
      joystickKnob: mustElement("joystick-knob"),
      boostButton: mustElement<HTMLButtonElement>("boost-button"),
      finaleScreen: mustElement("finale-screen"),
      finaleSize: mustElement("finale-size"),
      finaleRank: mustElement("finale-rank"),
      finaleTime: mustElement("finale-time"),
      finalePickups: mustElement("finale-pickups"),
      finaleRares: mustElement("finale-rares"),
      finaleTimeBonus: mustElement("finale-time-bonus"),
      finaleScore: mustElement("finale-score"),
      shareButton: mustElement<HTMLAnchorElement>("x-share-button"),
      continueButton: mustElement<HTMLButtonElement>("continue-button"),
      restartButton: mustElement<HTMLButtonElement>("restart-button"),
    };
  }

  private installDebugApi(enableMutationHooks: boolean): void {
    const snapshot = (): GameSnapshot => this.snapshot();
    const api: SolDebugApi = {
      snapshot,
      collectNearest: (): GameSnapshot => {
        if (!enableMutationHooks) {
          return snapshot();
        }
        const target = this.world.findNearestEligible(
          this.playerXMeters,
          this.playerZMeters,
          this.growth.radiusMeters * PICKUP_RADIUS_RATIO,
        );
        if (target) {
          this.playerXMeters = target.xMeters;
          this.playerZMeters = target.zMeters;
          this.velocityXMeters = 0;
          this.velocityZMeters = 0;
          this.world.updateStreaming(this.playerXMeters, this.playerZMeters);
          this.resolveCollisions();
        }
        this.updateUi();
        return snapshot();
      },
      collideOversized: (): OversizedCollisionProbe => {
        const before = snapshot();
        const maximumPickupRadiusMeters = before.radiusMeters * PICKUP_RADIUS_RATIO;
        if (!enableMutationHooks) {
          return {
            targetFound: false,
            targetId: null,
            targetRadiusMeters: null,
            maximumPickupRadiusMeters,
            targetWasOversized: false,
            targetActiveAfter: false,
            pickupDelta: 0,
            before,
            after: before,
          };
        }
        const target = this.world.findNearestOversized(
          this.playerXMeters,
          this.playerZMeters,
          maximumPickupRadiusMeters,
        );
        if (target) {
          this.playerXMeters = target.xMeters;
          this.playerZMeters = target.zMeters;
          this.velocityXMeters = 0;
          this.velocityZMeters = 0;
          this.world.updateStreaming(this.playerXMeters, this.playerZMeters);
          this.resolveCollisions();
        }
        this.updateUi();
        const after = snapshot();
        return {
          targetFound: Boolean(target),
          targetId: target?.id ?? null,
          targetRadiusMeters: target?.radiusMeters ?? null,
          maximumPickupRadiusMeters,
          targetWasOversized: Boolean(target && target.radiusMeters > maximumPickupRadiusMeters),
          targetActiveAfter: target?.active ?? false,
          pickupDelta: after.pickupCount - before.pickupCount,
          before,
          after,
        };
      },
      collideGoal: (): GoalCollisionProbe => {
        const before = snapshot();
        const targetFound = this.goalLandmark.spawned
          && this.activeTierIndex === TIERS.length - 1
          && !this.goalLandmark.absorbed;
        const goalReadyBefore = this.isGoalReady;
        if (enableMutationHooks && targetFound) {
          this.playerXMeters = this.goalLandmark.position.x;
          this.playerZMeters = this.goalLandmark.position.z;
          this.velocityXMeters = 0;
          this.velocityZMeters = 0;
          this.world.updateStreaming(this.playerXMeters, this.playerZMeters);
          this.resolveGoalCollision();
          this.updateUi();
        }
        const after = snapshot();
        return {
          targetFound,
          goalReadyBefore,
          goalAbsorbingAfter: after.goalAbsorbing,
          goalAbsorbedAfter: after.goalAbsorbed,
          before,
          after,
        };
      },
      forcePickup: (count = 1): GameSnapshot => {
        if (!enableMutationHooks) {
          return snapshot();
        }
        const iterations = clamp(Math.floor(count), 1, 500);
        for (let index = 0; index < iterations; index += 1) {
          const radiusBefore = this.growth.radiusMeters;
          const result = this.growth.absorb(radiusBefore * 0.69);
          if (!result.accepted) {
            continue;
          }
          const synthetic: WorldItem = {
            id: hashNumbers(0xde_b6, this.growth.pickupCount),
            chunkX: 0,
            chunkZ: 0,
            ordinal: index,
            xMeters: this.playerXMeters,
            zMeters: this.playerZMeters,
            surfaceYMeters: this.surfaceHeightMeters,
            radiusMeters: radiusBefore * 0.69,
            heightScale: 1,
            archetype: index % 5,
            colorIndex: index % 7,
            rotation: 0,
            name: "Debug Fragment",
            rare: false,
            rareId: null,
            active: false,
            renderSlot: -1,
          };
          this.handlePickup(synthetic, result);
        }
        this.updateUi();
        return snapshot();
      },
      forceRarePickup: (): GameSnapshot => {
        if (!enableMutationHooks || this.runStats.finished) {
          return snapshot();
        }
        const radiusBefore = this.growth.radiusMeters;
        const result = this.growth.absorb(radiusBefore * 0.69);
        if (result.accepted) {
          const rare: WorldItem = {
            id: hashNumbers(0x52_41_52_45, this.growth.pickupCount),
            chunkX: 0,
            chunkZ: 0,
            ordinal: 0,
            xMeters: this.playerXMeters,
            zMeters: this.playerZMeters,
            surfaceYMeters: this.surfaceHeightMeters,
            radiusMeters: radiusBefore * 0.69,
            heightScale: 1,
            archetype: 4,
            colorIndex: 6,
            rotation: 0,
            name: "Golden Maneki-neko",
            rare: true,
            rareId: 0,
            active: false,
            renderSlot: -1,
          };
          this.handlePickup(rare, result);
        }
        this.updateUi();
        return snapshot();
      },
      triggerDash: (): GameSnapshot => {
        if (enableMutationHooks) {
          const update = this.dash.update(0, true);
          this.dash.update(0, false);
          if (update.triggered) this.audio.dash();
          this.updateUi();
        }
        return snapshot();
      },
      forceGoalReady: (): GameSnapshot => {
        if (!enableMutationHooks) {
          return snapshot();
        }
        this.growth.setRadiusForDebug(GOAL_REQUIRED_RADIUS_METERS * 1.01);
        if (this.goalLandmark.spawned) {
          const dx = this.playerXMeters - this.goalLandmark.position.x;
          const dz = this.playerZMeters - this.goalLandmark.position.z;
          const distance = Math.hypot(dx, dz);
          const safeDistance =
            this.growth.radiusMeters
            + GOAL_OBJECT_RADIUS_METERS * 0.58
            + getTier(TIERS.length - 1).metersPerUnit * 0.5;
          if (distance < safeDistance) {
            const normalX = distance > 0.001 ? dx / distance : Math.sin(this.input.cameraYaw);
            const normalZ = distance > 0.001 ? dz / distance : Math.cos(this.input.cameraYaw);
            this.playerXMeters = this.goalLandmark.position.x + normalX * safeDistance;
            this.playerZMeters = this.goalLandmark.position.z + normalZ * safeDistance;
            this.velocityXMeters = 0;
            this.velocityZMeters = 0;
            this.world.updateStreaming(this.playerXMeters, this.playerZMeters);
          }
        }
        if (this.activeTierIndex !== TIERS.length - 1 && !this.transition) {
          this.beginTierTransition(TIERS.length - 1);
        }
        this.updateUi();
        return snapshot();
      },
      forceTier: (tierIndex: number): GameSnapshot => {
        if (!enableMutationHooks) {
          return snapshot();
        }
        const target = clamp(Math.floor(tierIndex), 0, TIERS.length - 1);
        const targetRadius = getTier(target).metersPerUnit * ENTER_RADIUS_UNITS * 1.03;
        this.growth.setRadiusForDebug(targetRadius);
        if (target !== this.activeTierIndex) {
          this.beginTierTransition(target);
        }
        this.updateUi();
        return snapshot();
      },
      step: (frames = 1): GameSnapshot => {
        if (!enableMutationHooks) {
          return snapshot();
        }
        const count = clamp(Math.floor(frames), 1, 60_000);
        for (let frame = 0; frame < count; frame += 1) {
          if (this.finalePaused) {
            break;
          }
          this.fixedUpdate(FIXED_STEP_SECONDS);
        }
        this.updateVisuals(FIXED_STEP_SECONDS);
        this.renderer.render(this.scene, this.camera);
        this.publishSnapshot();
        return snapshot();
      },
      setCameraYaw: (yawRadians: number): GameSnapshot => {
        if (enableMutationHooks && Number.isFinite(yawRadians)) {
          this.input.cameraYaw = yawRadians;
          this.updateVisuals(0);
          this.renderer.render(this.scene, this.camera);
          this.publishSnapshot();
        }
        return snapshot();
      },
      setAutoplay: (enabled: boolean): GameSnapshot => {
        if (enableMutationHooks) {
          this.autoplay = enabled;
          this.running = true;
          this.ui.intro.classList.add("hidden");
        }
        return snapshot();
      },
      restart: (): GameSnapshot => {
        if (enableMutationHooks) {
          this.restart();
        }
        return snapshot();
      },
      shareUrl: (): string | null => (
        this.runResult ? buildResultShareUrl(this.runResult, location.href) : null
      ),
    };
    window.__SOL_GAME__ = api;
  }

  private publishSnapshot(): void {
    window.__SOL_KATAMARI_METRICS__ = this.snapshot();
  }

  private get visualTierIndex(): number {
    if (this.transition && this.transition.progress >= 0.45) {
      return this.transition.toTierIndex;
    }
    return this.activeTierIndex;
  }

  private get isGoalReady(): boolean {
    return this.growth.canAbsorb(GOAL_OBJECT_RADIUS_METERS);
  }
}
